import { useEffect, useMemo, useRef, useState } from "react";

import {
  DeviceData,
  DeviceHealthData,
  GetServicesByLocationQuery,
  Service,
} from "../../../API";
import { useGetServicesByLocation } from "../../../common/hooks/useGetServicesByLocation";
import useLocationFromCache from "../../../common/hooks/useLocationFromCache";
import { AutocompleteOptionType } from "../../../common/models/autocomplete";
import {
  useSelectedLocationDropdown,
  useSelectedTagIdDropdown,
  useSelectedTagLocationsDropdown,
} from "../../../common/variables/selectedLocationDropdown";
import { useListenToDeviceHealthDataChange } from "../../deployment-health/hooks/useListenToDeviceHealthDataChange";
import { ServiceTypeEnum } from "../../model-manager/variables/modelManager";
import { useGetDevicesMakesCached } from "../../system-settings/tabs/devices/hooks/useGetDevicesMakesCached";
import {
  DisplayNameInput,
  servicesByLocationVariable,
  useServicesByLocationVariable,
} from "../variables/devices";
import { useGetDevicesCached } from "./useGetDevicesCached";

export interface IModel {
  serviceName: ServiceTypeEnum;
  serviceId: string;
  nodeId: string;
  isRunning?: boolean;
  configuration?: string;
}

export type DeviceDataType = Omit<DeviceData, "__typename">;

export interface IDeviceTableRow {
  rowId: string;
  name: string;
  location: DisplayNameInput;
  node: DisplayNameInput;
  makeModelId: string;
  models: IModel[];
  modelsToRun: string[];
  deviceData: DeviceDataType;
  healthData: Omit<DeviceHealthData, "__typename">;
  isOnline: boolean;
  cameraIpAddress: string;
  rtspTemplate: string;
}

interface IUseDeviceTableRows {
  rows: IDeviceTableRow[];
  loading: boolean;
}

export const useDeviceTableRows = (): IUseDeviceTableRows => {
  const selectedLocation = useSelectedLocationDropdown();
  const selectedTagLocations = useSelectedTagLocationsDropdown();
  const selectedTagId = useSelectedTagIdDropdown();
  const servicesVariable = useServicesByLocationVariable();

  const { data: deviceHealthData } = useListenToDeviceHealthDataChange();

  const currentTagIdRef = useRef("");

  const { data, loading: deviceLoading, refetch } = useGetDevicesCached();
  const {
    data: servicesData,
    loading: servicesLoading,
    refetchLoading,
    fetchServicesByLocationId,
  } = useGetServicesByLocation(selectedLocation?.value);

  const { getCachedLocation } = useLocationFromCache();
  const { data: devicesMakesData, loading: devicesMakesDataLoading } =
    useGetDevicesMakesCached();

  const isLoading =
    deviceLoading ||
    servicesLoading ||
    refetchLoading ||
    devicesMakesDataLoading;

  const services = useMemo(() => {
    if (!servicesData || selectedTagId?.length) return [];

    const servicesByTag = servicesData?.getServicesByLocation?.items ?? [];

    servicesByLocationVariable(servicesByTag as Service[]);

    return servicesData?.getServicesByLocation?.items ?? [];
  }, [servicesVariable, servicesData]);

  const rowsByLocation: IDeviceTableRow[] = useMemo(() => {
    const mappedData = !isLoading
      ? data?.getDevices?.items?.map((item): any => {
          const models =
            services
              ?.filter(service => {
                // note: item is device i.e. item.id will always be DE#<>
                // legacy service item's deviceId field has no C# prefix e.g. DE#<>
                const doesLegacyIdsMatch = service?.deviceId === item?.id;
                // service item's deviceId field has C# prefix e.g. C#<>DE#<>; must remove before comparison
                const doesIdsMatch =
                  `DE#${service?.deviceId?.split("DE#")[1]}` === item?.id;

                return doesLegacyIdsMatch || doesIdsMatch;
              })
              .map(service => ({
                serviceName: service?.serviceType,
                serviceId: service?.id,
                nodeId: service?.nodeId,
                isRunning: service?.isRunning,
              })) ?? [];

          const cachedLocation = getCachedLocation(item?.locationId ?? "");

          const foundDeviceMake = devicesMakesData?.getDevicesMakes.items.find(
            el => el?.model === item?.makeModelId
          );

          return {
            rowId: item?.id as string,
            name: item?.name as string,
            location: {
              id: item?.locationId as string,
              name: cachedLocation?.name as string,
            },
            node: {
              id: item?.nodeId as string,
              name: item?.nodeName as string,
            },
            modelsToRun: foundDeviceMake?.modelsToRun,
            makeModelId: item?.makeModelId as string,
            models,
            deviceData: item?.deviceData,
            healthData: item?.healthData ?? "{}",
            isOnline: Boolean(item?.healthData?.isOnline),
            cameraIpAddress: item?.cameraIpAddress as string,
          };
        }) ?? []
      : [];

    let rows = [...mappedData];

    if (selectedLocation?.value) {
      rows = rows?.filter((item: any) => {
        const nameOfLocation = item.location.id;

        return nameOfLocation === selectedLocation?.value;
      });
    }

    return rows;
  }, [
    data?.getDevices?.items,
    services,
    window.location.hash,
    isLoading,
    selectedLocation?.value,
  ]);

  const [servicesByTagLocations, setServicesByTagLocations] = useState<
    (GetServicesByLocationQuery | undefined)[]
  >([]);

  useEffect(() => {
    const fetchServices = async () => {
      const tagLocations = selectedTagLocations ?? [];

      if (!isLoading && currentTagIdRef.current !== selectedTagId) {
        try {
          const results = await Promise.all(
            tagLocations.map(location =>
              fetchServicesByLocationId(location?.value ?? "")
            )
          );

          setServicesByTagLocations(results.filter(Boolean));

          const servicesByTag = results
            .map(service => {
              return service?.getServicesByLocation?.items ?? null;
            })
            .flat()
            .filter(service => service !== null);

          servicesByLocationVariable(servicesByTag as Service[]);

          currentTagIdRef.current = selectedTagId ?? "";
        } catch (error) {
          console.error("Error fetching services:", error);

          setServicesByTagLocations([]);

          servicesByLocationVariable([]);
        }
      }
    };

    fetchServices();
  }, [isLoading, selectedTagLocations, selectedTagId]);

  useEffect(() => {
    return () => {
      // Clear the servicesByLocation variable when the component is unmounted
      servicesByLocationVariable([]);
    };
  }, []);

  const rowsByTagLocations: IDeviceTableRow[] = useMemo(() => {
    const servicesByTag = servicesByTagLocations
      .map(service => {
        return service?.getServicesByLocation?.items ?? null;
      })
      .flat()
      .filter(service => service !== null);

    const mappedData =
      !isLoading && selectedTagLocations?.length
        ? data?.getDevices?.items?.map((item): IDeviceTableRow => {
            const models =
              servicesByTag
                .filter(service => {
                  // note: item is device i.e. item.id will always be DE#<>
                  // legacy service item's deviceId field has no C# prefix e.g. DE#<>
                  const serviceByDeviceMatch =
                    service?.deviceId === item?.id ||
                    `DE#${service?.deviceId?.split("DE#")[1]}` === item?.id;

                  return serviceByDeviceMatch;
                })
                .map(service => {
                  return {
                    serviceName: service?.serviceType,
                    serviceId: service?.id,
                    nodeId: service?.nodeId,
                    isRunning: service?.isRunning,
                  };
                }) ?? [];

            const cachedLocation = getCachedLocation(item?.locationId ?? "");

            const foundDeviceMake =
              devicesMakesData?.getDevicesMakes.items.find(
                el => el?.model === item?.makeModelId
              );

            return {
              rowId: item?.id as string,
              name: item?.name as string,
              location: {
                id: item?.locationId as string,
                name: cachedLocation?.name as string,
              },
              node: {
                id: item?.nodeId as string,
                name: item?.nodeName as string,
              },
              modelsToRun: foundDeviceMake?.modelsToRun as [string],
              makeModelId: item?.makeModelId as string,
              models: models as IModel[],
              deviceData: item!.deviceData,
              healthData: item!.healthData,
              rtspTemplate: foundDeviceMake?.rtspTemplate ?? "",
              isOnline: Boolean(item?.healthData?.isOnline),
              cameraIpAddress: item?.cameraIpAddress as string,
            };
          }) ?? []
        : [];

    let rows = [...mappedData];

    if (selectedTagLocations?.length) {
      rows = rows?.filter((item: any) => {
        const nameOfLocation = item.location.id;

        const locations = selectedTagLocations.map(
          (tag: AutocompleteOptionType) => {
            if (nameOfLocation.includes("#L#")) {
              return tag?.value?.toLowerCase() ?? "";
            }

            return tag?.title?.toLowerCase() ?? "";
          }
        );

        return locations.includes(nameOfLocation.toLowerCase());
      });
    }

    if (selectedTagLocations && selectedTagLocations.length === 0) {
      rows = [];
    }

    if (!selectedTagLocations) {
      rows = mappedData;
    }

    return rows;
  }, [
    data?.getDevices?.items,
    isLoading,
    selectedTagLocations,
    servicesByTagLocations,
  ]);

  const rows = selectedLocation?.value ? rowsByLocation : rowsByTagLocations;

  const memoizedDevicesRows =
    useMemo(() => {
      const isDeviceDataFromSubscription =
        deviceHealthData?.listenToDeviceHealthData;

      if (!isDeviceDataFromSubscription) return rows;

      const updatedRows = rows.map(device => {
        if (isDeviceDataFromSubscription.id === device?.rowId) {
          // Check if the online status has changed
          if (
            isDeviceDataFromSubscription?.healthData?.isOnline !==
            device?.healthData?.isOnline
          ) {
            refetch();
          }

          return {
            ...device,
            isOnline: Boolean(
              isDeviceDataFromSubscription?.healthData?.isOnline
            ),
            models: device.models.map(model => ({
              ...model,
              isRunning:
                services.find(service => service?.id === model.serviceId)
                  ?.isRunning ?? false,
            })),
          };
        }

        return {
          ...device,
          models: device.models.map(model => ({
            ...model,
            isRunning:
              services.find(service => service?.id === model.serviceId)
                ?.isRunning ?? false,
          })),
        };
      });

      return updatedRows;
    }, [deviceHealthData, rows, services]) ?? [];

  const sortedRows = [...memoizedDevicesRows]
    ?.sort((a, b) => {
      return a.name.localeCompare(b.name);
    })
    .map((item, i) => ({ ...item, sequenceNumber: i + 1 }));

  return {
    rows: sortedRows,
    loading: isLoading,
  };
};
